import {createRule, type Maybe} from '@regle/core';
import {minValue} from '@regle/rules';
import {useFormat, useTranslation} from '#imports';
import type {Currency} from '../../common';

/**
 * Requires a field to have a specified minimum price value.
 *
 * @param minPrice - the minimum price
 * @param currency - the currency to display in the error message
 *
 * @example
 * ```ts
 *  import { useRegle, minPrice } from '@malt/forms';
 *
 *  const user = ref<User>();
 *
 *  const { r$ } = useRegle(... , {
 *    amount: { minPrice: minPrice(100, user.currency)}
 *  })
 * ```
 */
export const minPrice = createRule({
    type: 'minValue',
    validator: (value: Maybe<number>, minPrice: number, _currency: Currency = 'EUR', options?: {allowEqual: boolean}) => {
        return minValue(minPrice, options).exec(value);
    },
    message: ({$params: [minPrice, currency, options]}) => {
        const {t} = useTranslation();
        const {formatAmount} = useFormat();

        return (options?.allowEqual ?? true)
            ? t('generic.forms.validation.errors.minValue.equal', [formatAmount(currency, minPrice)])
            : t('generic.forms.validation.errors.minValue', [formatAmount(currency, minPrice)]);
    },
});
